/**
 * Notification preferences — per-type (in_app, push) toggles.
 *
 * The server tells us which types exist (via /me/notification-prefs)
 * so adding a new notification type lands automatically — no client
 * change required. We render one row per type with its icon, a human
 * label derived from the type key, and two switches.
 *
 * Toggles fire individually via PATCH /me/notification-prefs/<type>;
 * we optimistically flip the local state and roll back on error.
 */

import { I, icon } from '../components/icons.js';
import { escape } from '../components/form.js';
import { toast } from '../components/toast.js';
import { navigate } from '../router.js';
import {
  fetchPrefs,
  patchPref,
} from '../lib/notifications.js';
import { ApiError } from '../api.js';

let _state = null;

export async function renderNotificationPrefs(root) {
  _state = { prefs: [], loading: true, error: false };
  root.innerHTML = shell();
  paint();
  await load();
  paint();
}

function shell() {
  return `
    <div class="appbar">
      <a href="/notifications" data-link class="back" aria-label="Back"
         style="text-decoration:none;color:inherit;">
        ${icon(I.back, { size: 18 })}
      </a>
      <div class="t">Notification preferences</div>
      <div class="sp"></div>
    </div>
    <div class="pad" id="prefs-body">
      <div class="muted" style="font-size:11.5px;margin-bottom:12px;">
        Turn each type off in the centre, on push, or both. Mute does
        not delete past notifications — they stay in the centre.
      </div>
      <div id="prefs-list">
        <div class="skel"></div>
        <div class="skel"></div>
        <div class="skel"></div>
      </div>
    </div>
  `;
}

async function load() {
  try {
    const res = await fetchPrefs();
    _state.prefs = res?.prefs || [];
    _state.loading = false;
  } catch (err) {
    _state.loading = false;
    _state.error = true;
    if (err instanceof ApiError && err.status === 401) {
      navigate('/login');
      return;
    }
    toast.fromError(err, 'Could not load preferences.');
  }
}

function paint() {
  const list = document.getElementById('prefs-list');
  if (!list) return;
  if (_state.loading) return;
  if (_state.error) {
    list.innerHTML = `<div class="card muted">Could not load — pull to refresh.</div>`;
    return;
  }
  if (!_state.prefs.length) {
    list.innerHTML = `<div class="card muted">No notification types registered.</div>`;
    return;
  }
  list.innerHTML = _state.prefs.map(rowHtml).join('');
  wireToggles();
}

function rowHtml(pref) {
  const iconSvg = iconForKey(pref.icon);
  return `
    <div class="card" style="padding:12px;" data-pref-type="${escape(pref.type)}">
      <div class="row">
        <div class="ic" style="${iconWrapStyle(pref.type)}">${iconSvg}</div>
        <div class="grow" style="min-width:0;">
          <b style="font-size:13px;color:#fff;font-weight:600;display:block;">
            ${escape(labelForType(pref.type))}
          </b>
          <div style="font-size:11px;color:var(--mute);font-weight:500;margin-top:1px;">
            ${escape(descriptionForType(pref.type))}
          </div>
        </div>
      </div>
      <div class="row" style="gap:8px;margin-top:10px;font-size:12px;">
        ${toggleHtml(pref.type, 'in_app', pref.in_app, 'In-app')}
        ${toggleHtml(pref.type, 'push', pref.push, 'Push')}
      </div>
    </div>
  `;
}

function toggleHtml(type, field, value, label) {
  // Plain <label><input type=checkbox> — minimal, accessible, works
  // without a custom switch component. CSS-only "switch" styling lives
  // in app.css elsewhere if we want to upgrade later.
  return `
    <label style="flex:1;width:auto;display:inline-flex;align-items:center;gap:8px;
                  padding:8px 10px;border:1px solid #1F1A33;border-radius:10px;
                  cursor:pointer;user-select:none;background:#16122A;">
      <input type="checkbox" data-pref-toggle data-field="${field}"
             ${value ? 'checked' : ''}
             style="width:16px;height:16px;cursor:pointer;accent-color:#B7A6FF;">
      <span style="color:#fff;font-weight:600;">${escape(label)}</span>
    </label>
  `;
}

function iconForKey(key) {
  const map = {
    bell: I.bell,
    friend: I.friends,
    swords: I.swords,
    trophy: I.trophy,
    eraser: I.eraser,
  };
  return icon(map[key] || I.bell, { size: 16 });
}

function iconWrapStyle(type) {
  if (type === 'duel_result' || type === 'eraser_awarded') {
    return 'background:#3A2E12;color:#F7C667;';
  }
  if (type.startsWith('friend_')) {
    return 'background:#251E48;color:#B7A6FF;';
  }
  if (type.startsWith('duel_')) {
    return 'background:#0F352A;color:#5FE0BE;';
  }
  if (type.startsWith('live_trivia')) {
    return 'background:#3A1528;color:#FF8FB6;';
  }
  return 'background:#251E48;color:#B7A6FF;';
}

const TYPE_LABELS = {
  friend_request_received: 'Friend requests',
  friend_request_accepted: 'Friend request accepted',
  live_trivia_announce: 'New live trivia',
  live_trivia_reminder: 'Live trivia reminders',
  duel_challenge_received: 'Incoming duel challenge',
  duel_host_joined: 'Duel opponent joined',
  duel_accepted: 'Your duel was accepted',
  duel_declined: 'Your duel was declined',
  duel_result: 'Duel results',
  eraser_awarded: 'Eraser awarded',
};

const TYPE_DESCRIPTIONS = {
  friend_request_received: 'Someone wants to add you.',
  friend_request_accepted: 'Your request was accepted.',
  live_trivia_announce: 'A new live trivia opened for registration.',
  live_trivia_reminder: '5 and 30 minutes before a live trivia you registered for.',
  duel_challenge_received: 'A friend challenged you to a 1-on-1.',
  duel_host_joined: 'Your shared-link duel got a guest.',
  duel_accepted: 'Your opponent accepted the duel.',
  duel_declined: 'Your opponent declined the duel.',
  duel_result: 'How a duel you played wrapped up.',
  eraser_awarded: 'You earned an eraser power-up.',
};

function labelForType(type) {
  return TYPE_LABELS[type] || humanize(type);
}

function descriptionForType(type) {
  return TYPE_DESCRIPTIONS[type] || '';
}

function humanize(type) {
  return type
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function wireToggles() {
  document.querySelectorAll('[data-pref-toggle]').forEach((input) => {
    input.addEventListener('change', async (ev) => {
      const row = input.closest('[data-pref-type]');
      const type = row?.dataset?.prefType;
      const field = input.dataset.field;
      if (!type || !field) return;
      const previous = !input.checked;
      const next = input.checked;
      try {
        const patched = await patchPref(type, { [field]: next });
        // Keep local state in lockstep with the server (it returns the
        // full resolved row, including the other field which we didn't
        // touch).
        const idx = _state.prefs.findIndex((p) => p.type === type);
        if (idx >= 0) {
          _state.prefs[idx] = { ..._state.prefs[idx], ...patched };
        }
      } catch (err) {
        // Roll back the visible state and surface a toast.
        input.checked = previous;
        toast.fromError(err, 'Could not update.');
      }
    });
  });
}
